import {
  Box,
  Collapse,
  CustomIcons,
  type SvgIconProps,
} from '@ltvco/refresh-lib/theme';
import BusinessIcon from '@mui/icons-material/Business';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhoneIcon from '@mui/icons-material/Phone';
import SchoolIcon from '@mui/icons-material/School';
import { useState, type ComponentType } from 'react';

import {
  Chevron,
  ExpandPanelInner,
  ExpandRow,
  ExpandRowCount,
  ExpandRowLabel,
  ExpandRowMain,
  ExpandRowPreview,
  OwnerAvatar,
  OwnerCard,
  OwnerCardContent,
  OwnerCardHeader,
  OwnerCardName,
  PreviewCardSection,
  OwnerReportBar,
  OwnerReportButton,
  OwnerReportText,
  StaticExpandRow,
} from '../../components';
import { SocialTwitterIcon } from '../../SocialMediaIcons';
import { formatPhoneLinkType, formatPhoneNumber, getInitials } from '../utils';

import {
  MoreOwnersButton,
  PlatformIcon,
  PlatformInitial,
  PlatformLink,
  PlatformName,
  PlatformRow,
  PlatformStatus,
} from './components';

import type {
  UsernameContactAddress,
  UsernameContactPhone,
  UsernameEducation,
  UsernameJob,
  UsernameReportData,
} from 'components/DeepSearch/types';

// ─── platform icon map ────────────────────────────────────────────────────────

const {
  RedondedFacebookIcon,
  RoundInstagramIcon,
  RedditIcon,
  SnapchatIcon,
  TelegramIcon,
  TikTokIcon,
  YouTubeIcon,
} = CustomIcons;

const PLATFORM_ICON_MAP: Record<string, ComponentType<SvgIconProps>> = {
  instagram: RoundInstagramIcon,
  facebook: RedondedFacebookIcon,
  reddit: RedditIcon,
  snapchat: SnapchatIcon,
  telegram: TelegramIcon,
  tiktok: TikTokIcon,
  youtube: YouTubeIcon,
  twitter: SocialTwitterIcon,
  'x (twitter)': SocialTwitterIcon,
  x: SocialTwitterIcon,
};

function getPlatformIcon(name: string): ComponentType<SvgIconProps> | null {
  return PLATFORM_ICON_MAP[name.toLowerCase()] ?? null;
}

function getPlatformStatusLabel(status: string): string {
  const v = status.toLowerCase();
  if (v === 'error' || v === 'unknown') {
    return 'Unknown';
  }
  if (v === 'taken') {
    return 'Taken';
  }
  if (v === 'available') {
    return 'Available';
  }
  if (v === 'invalid') {
    return 'Invalid';
  }
  return status;
}

function hasExternalLink(status: string): boolean {
  const v = status.toLowerCase();
  return v === 'taken' || v === 'available';
}

// ─── helpers ──────────────────────────────────────────────────────────────────

const contactIconSx = { fontSize: 18, color: 'text.secondary', flexShrink: 0 };

// ─── UsernamePlatformSection ──────────────────────────────────────────────────

export const UsernamePlatformSection = ({
  platforms,
}: {
  platforms: UsernameReportData['platforms'];
}) => (
  <PreviewCardSection title="Platform availability">
    <Box>
      {platforms.map((platform) => {
        const IconComponent = getPlatformIcon(platform.name);
        const statusLabel = getPlatformStatusLabel(platform.status);
        const showLink =
          hasExternalLink(platform.status) && Boolean(platform.url);
        let safeUrl: string | undefined;
        if (showLink && platform.url) {
          try {
            const parsed = new URL(platform.url);
            if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
              safeUrl = platform.url;
            }
          } catch {
            // malformed URL — no link
          }
        }
        return (
          <PlatformRow key={platform.name}>
            <PlatformIcon>
              {IconComponent ? (
                <IconComponent sx={{ width: 28, height: 28 }} />
              ) : (
                <PlatformInitial>
                  {platform.name.charAt(0).toUpperCase()}
                </PlatformInitial>
              )}
            </PlatformIcon>
            <PlatformName variant="body1">{platform.name}:</PlatformName>
            <PlatformStatus variant="body1">{statusLabel}</PlatformStatus>
            {safeUrl ? (
              <PlatformLink href={safeUrl} target="_blank" rel="noreferrer">
                <OpenInNewIcon sx={{ fontSize: 18 }} />
              </PlatformLink>
            ) : (
              <Box sx={{ width: 18, flexShrink: 0 }} />
            )}
          </PlatformRow>
        );
      })}
    </Box>
  </PreviewCardSection>
);

// ─── inner expand rows ────────────────────────────────────────────────────────

const AddressExpandRow = ({
  addresses,
}: {
  addresses: UsernameContactAddress[];
}) => {
  const [open, setOpen] = useState(false);
  if (addresses.length === 0) {
    return (
      <Box>
        <StaticExpandRow>
          <ExpandRowMain>
            <ExpandRowLabel>Possible addresses</ExpandRowLabel>
          </ExpandRowMain>
          <ExpandRowCount count={0} />
          <Chevron open={false} flexShrink={0} hidden />
        </StaticExpandRow>
      </Box>
    );
  }
  const preview = addresses
    .slice(0, 2)
    .map((a) => {
      const parts = a.full.split(',');
      return parts.length >= 2 ? parts.slice(-2).join(',').trim() : a.full;
    })
    .join(' · ');
  return (
    <Box>
      <ExpandRow
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <ExpandRowMain>
          <ExpandRowLabel>Possible addresses</ExpandRowLabel>
          {preview && <ExpandRowPreview>{preview}</ExpandRowPreview>}
        </ExpandRowMain>
        <ExpandRowCount count={addresses.length} />
        <Chevron open={open} flexShrink={0} />
      </ExpandRow>
      <Collapse in={open}>
        <ExpandPanelInner>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {addresses.map((addr, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'flex-start',
                  padding: '8px 0',
                  borderBottom: i < addresses.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <LocationOnIcon sx={contactIconSx} />
                <Box sx={{ minWidth: 0 }}>
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      fontSize: 16,
                      fontWeight: 700,
                      color: 'text.primary',
                      lineHeight: 1.4,
                    }}
                  >
                    {addr.full}
                  </Box>
                  {addr.type && (
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        fontSize: 12,
                        color: 'text.disabled',
                        mt: '1px',
                      }}
                    >
                      {addr.type === 'work' ? 'Work' : 'Primary address'}
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </ExpandPanelInner>
      </Collapse>
    </Box>
  );
};

const PhoneExpandRow = ({ phones }: { phones: UsernameContactPhone[] }) => {
  const [open, setOpen] = useState(false);
  if (phones.length === 0) {
    return (
      <Box>
        <StaticExpandRow>
          <ExpandRowMain>
            <ExpandRowLabel>Possible phone numbers</ExpandRowLabel>
          </ExpandRowMain>
          <ExpandRowCount count={0} />
          <Chevron open={false} flexShrink={0} hidden />
        </StaticExpandRow>
      </Box>
    );
  }
  const preview = phones[0]?.number ? formatPhoneNumber(phones[0].number) : '';
  return (
    <Box>
      <ExpandRow
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <ExpandRowMain>
          <ExpandRowLabel>Possible phone numbers</ExpandRowLabel>
          {preview && <ExpandRowPreview>{preview}</ExpandRowPreview>}
        </ExpandRowMain>
        <ExpandRowCount count={phones.length} />
        <Chevron open={open} flexShrink={0} />
      </ExpandRow>
      <Collapse in={open}>
        <ExpandPanelInner>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {phones.map((phone, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'flex-start',
                  padding: '8px 0',
                  borderBottom: i < phones.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <PhoneIcon sx={contactIconSx} />
                <Box sx={{ minWidth: 0 }}>
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      fontSize: 16,
                      fontWeight: 700,
                      color: 'text.primary',
                      lineHeight: 1.4,
                    }}
                  >
                    {formatPhoneNumber(phone.number)}
                  </Box>
                  {phone.type && (
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        fontSize: 12,
                        color: 'text.disabled',
                        mt: '1px',
                      }}
                    >
                      {formatPhoneLinkType(phone.type)}
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </ExpandPanelInner>
      </Collapse>
    </Box>
  );
};

const JobsEducationExpandRow = ({
  jobs,
  education,
}: {
  jobs: UsernameJob[];
  education: UsernameEducation[];
}) => {
  const [open, setOpen] = useState(false);
  const count = jobs.length + education.length;
  if (count === 0) {
    return (
      <Box>
        <StaticExpandRow>
          <ExpandRowMain>
            <ExpandRowLabel>Jobs &amp; education</ExpandRowLabel>
          </ExpandRowMain>
          <ExpandRowCount count={0} />
          <Chevron open={false} flexShrink={0} hidden />
        </StaticExpandRow>
      </Box>
    );
  }
  const previewParts = [jobs[0]?.company, education[0]?.school].filter(Boolean);
  const preview = previewParts.slice(0, 2).join(' · ');

  const allItems: Array<{
    icon: typeof BusinessIcon;
    title: string;
    sub: string;
    date: string;
  }> = [
    ...jobs.map((j) => ({
      icon: BusinessIcon,
      title: j.company,
      sub: j.titles.join(', '),
      date: j.period,
    })),
    ...education.map((e) => ({
      icon: SchoolIcon,
      title: e.school,
      sub: e.degrees.join(', '),
      date: e.attended,
    })),
  ];

  return (
    <Box>
      <ExpandRow
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <ExpandRowMain>
          <ExpandRowLabel>Jobs &amp; education</ExpandRowLabel>
          {preview && <ExpandRowPreview>{preview}</ExpandRowPreview>}
        </ExpandRowMain>
        <ExpandRowCount count={count} />
        <Chevron open={open} flexShrink={0} />
      </ExpandRow>
      <Collapse in={open}>
        <ExpandPanelInner>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {allItems.map((item, i) => {
              const Icon = item.icon;
              return (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'flex-start',
                    padding: '10px 0',
                    borderBottom:
                      i < allItems.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}
                >
                  <Icon
                    sx={{
                      fontSize: 18,
                      color: 'text.secondary',
                      flexShrink: 0,
                      mt: '2px',
                    }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        fontSize: 16,
                        fontWeight: 700,
                        color: 'text.primary',
                        lineHeight: 1.4,
                      }}
                    >
                      {item.title}
                    </Box>
                    {item.sub && (
                      <Box
                        component="span"
                        sx={{
                          display: 'block',
                          fontSize: 14,
                          color: 'text.secondary',
                          lineHeight: 1.45,
                          mt: '2px',
                        }}
                      >
                        {item.sub}
                      </Box>
                    )}
                    {item.date && (
                      <Box
                        component="span"
                        sx={{
                          display: 'block',
                          fontSize: 12,
                          color: 'text.disabled',
                          mt: '1px',
                        }}
                      >
                        {item.date}
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </ExpandPanelInner>
      </Collapse>
    </Box>
  );
};

// ─── owner accordion card ─────────────────────────────────────────────────────

const OwnerAccordionCard = ({
  ownerName,
  isPrimary,
  data,
  onViewReport,
}: {
  ownerName: string;
  isPrimary: boolean;
  data: UsernameReportData;
  onViewReport?: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const addresses = isPrimary ? data.potentialAddresses : [];
  const phones = isPrimary ? data.potentialPhoneNumbers : [];
  const jobs = isPrimary ? data.jobs : [];
  const education = isPrimary ? data.education : [];

  return (
    <OwnerCard>
      <OwnerCardHeader
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <OwnerAvatar>{getInitials(ownerName)}</OwnerAvatar>
        <OwnerCardName>{ownerName}</OwnerCardName>
        <Chevron open={open} flexShrink={0} />
      </OwnerCardHeader>
      <Collapse in={open}>
        <OwnerCardContent>
          {onViewReport && (
            <OwnerReportBar type="button" onClick={onViewReport}>
              <OwnerReportText>
                View a complete report for {ownerName}
              </OwnerReportText>
              <OwnerReportButton>
                <ChevronRightIcon sx={{ fontSize: 20 }} />
              </OwnerReportButton>
            </OwnerReportBar>
          )}
          <AddressExpandRow addresses={addresses} />
          <PhoneExpandRow phones={phones} />
          <JobsEducationExpandRow jobs={jobs} education={education} />
        </OwnerCardContent>
      </Collapse>
    </OwnerCard>
  );
};

// ─── UsernameOwnersSection ────────────────────────────────────────────────────

const VISIBLE_OWNERS = 5;

export const UsernameOwnersSection = ({
  data,
  onViewReportForOwner,
}: {
  data: UsernameReportData;
  onViewReportForOwner?: (ownerName: string, bvId?: string) => void;
}) => {
  const [showAll, setShowAll] = useState(false);

  const primaryName = data.potentialOwner?.name ?? data.ownerName;
  const otherNames = data.otherPotentialOwners;

  const allOwners: Array<{ name: string; bvId?: string }> = [];
  if (primaryName) {
    allOwners.push({ name: primaryName, bvId: data.potentialOwner?.bvId });
  }
  for (const owner of otherNames) {
    if (!allOwners.find((o) => o.name === owner.name)) {
      allOwners.push({ name: owner.name, bvId: owner.bvId });
    }
  }

  const visible = showAll ? allOwners : allOwners.slice(0, VISIBLE_OWNERS);
  const moreCount = allOwners.length - VISIBLE_OWNERS;

  return (
    <PreviewCardSection title="Possible owners">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {visible.map((owner, i) => (
          <OwnerAccordionCard
            key={`${owner.name}-${i}`}
            ownerName={owner.name}
            isPrimary={i === 0}
            data={data}
            onViewReport={
              onViewReportForOwner
                ? () => onViewReportForOwner(owner.name, owner.bvId)
                : undefined
            }
          />
        ))}
      </Box>
      {moreCount > 0 && (
        <MoreOwnersButton type="button" onClick={() => setShowAll((v) => !v)}>
          {!showAll && (
            <Box
              component="span"
              sx={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}
            >
              +
            </Box>
          )}
          <Box component="span" sx={{ flex: 1, textAlign: 'left' }}>
            {showAll
              ? 'Show fewer'
              : `${moreCount} more possible owner${moreCount !== 1 ? 's' : ''}`}
          </Box>
        </MoreOwnersButton>
      )}
    </PreviewCardSection>
  );
};
